import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, ArrowLeft, Calendar, User, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';
import blogPosts from '../data/blogPosts';

const CAL_LINK = "https://cal.com/muhammad-furqan-karim-kidwai-uvsohz/30min";

// Internal link mapping for SEO
const internalLinks = {
  'accounts payable': { href: '/use-cases/accounts-payable-automation', text: 'accounts payable' },
  'financial close software': { href: '/blog/financial-close-software-ai-agents-automate-month-end', text: 'financial close software' },
  'SOC 2': { href: '/security', text: 'SOC 2' },
  'security': { href: '/security', text: 'security' },
  'Plouton AI': { href: '/', text: 'Plouton AI' },
  'bank reconciliation': { href: '/use-cases/accounts-payable-automation', text: 'bank reconciliation' },
};

const renderLinkedText = (text) => {
  let result = text;
  const parts = [];
  let lastIndex = 0;
  
  // Find all link opportunities (case-insensitive, first occurrence only)
  const linkEntries = Object.entries(internalLinks);
  const matches = [];
  
  linkEntries.forEach(([keyword, linkData]) => {
    const idx = result.toLowerCase().indexOf(keyword.toLowerCase());
    if (idx !== -1) {
      matches.push({ start: idx, end: idx + keyword.length, linkData, original: result.slice(idx, idx + keyword.length) });
    }
  });
  
  // Sort by position and filter overlaps
  matches.sort((a, b) => a.start - b.start);
  const filtered = [];
  let lastEnd = 0;
  matches.forEach(m => {
    if (m.start >= lastEnd) {
      filtered.push(m);
      lastEnd = m.end;
    }
  });
  
  if (filtered.length === 0) return text;
  
  filtered.forEach((match, i) => {
    if (match.start > lastIndex) {
      parts.push(result.slice(lastIndex, match.start));
    }
    parts.push(
      <Link key={i} to={match.linkData.href} className="text-blue-400 hover:text-blue-300 underline decoration-blue-400/30 hover:decoration-blue-300 transition-colors">
        {match.original}
      </Link>
    );
    lastIndex = match.end;
  });
  
  if (lastIndex < result.length) {
    parts.push(result.slice(lastIndex));
  }
  
  return parts.length > 0 ? parts : text;
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const [progress, setProgress] = useState(0);
  const articleRef = useRef(null);
  const post = blogPosts.find(p => p.slug === slug);

  useEffect(() => {
    const handleScroll = () => {
      if (!articleRef.current) return;
      const articleTop = articleRef.current.offsetTop;
      const articleHeight = articleRef.current.offsetHeight;
      const windowHeight = window.innerHeight;
      const scrolled = window.scrollY - articleTop + windowHeight * 0.3;
      const pct = Math.min(Math.max((scrolled / articleHeight) * 100, 0), 100);
      setProgress(pct);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Set document title and meta for SEO
  useEffect(() => {
    if (post) {
      document.title = `${post.metaTitle} | Plouton AI`;
      const setMeta = (name, content) => {
        if (!content) return;
        let el = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
        if (!el) {
          el = document.createElement('meta');
          el.setAttribute(name.startsWith('og:') || name.startsWith('twitter:') ? 'property' : 'name', name);
          document.head.appendChild(el);
        }
        el.setAttribute('content', content);
      };
      const setLink = (rel, href) => {
        let el = document.querySelector(`link[rel="${rel}"]`);
        if (!el) { el = document.createElement('link'); el.setAttribute('rel', rel); document.head.appendChild(el); }
        el.setAttribute('href', href);
      };
      const url = `https://plouton.co/blog/${post.slug}`;
      setMeta('description', post.metaDescription);
      setLink('canonical', url);
      setMeta('og:title', `${post.metaTitle} | Plouton AI`);
      setMeta('og:description', post.metaDescription);
      setMeta('og:url', url);
      setMeta('og:image', post.heroImage);
      setMeta('og:type', 'article');
      setMeta('og:site_name', 'Plouton AI');
      setMeta('twitter:card', 'summary_large_image');
      setMeta('twitter:title', `${post.metaTitle} | Plouton AI`);
      setMeta('twitter:description', post.metaDescription);
      setMeta('twitter:image', post.heroImage);
      setMeta('article:published_time', post.publishDate);
      setMeta('article:author', post.author);
    }
    return () => {
      document.title = 'Plouton AI | Autonomous Finance Workers';
    };
  }, [post]);

  if (!post) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] pt-20 flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-heading text-2xl font-bold text-white mb-4">Post not found</h2>
          <Link to="/blog" className="text-blue-400 hover:underline">Back to blog</Link>
        </div>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // JSON-LD structured data for SEO
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.metaDescription,
    "image": post.heroImage,
    "author": {
      "@type": "Person",
      "name": post.author,
      "jobTitle": post.authorRole,
      "url": "https://plouton.co/company"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Plouton AI",
      "url": "https://plouton.co",
      "logo": {
        "@type": "ImageObject",
        "url": "https://customer-assets.emergentagent.com/job_64eb10e1-fdde-4977-afc2-4761fbf5691b/artifacts/5pkoywp1_Plouton_Logo%20%281%29.png"
      }
    },
    "datePublished": post.publishDate,
    "dateModified": post.publishDate,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://plouton.co/blog/${post.slug}`
    },
    "keywords": post.keywords.join(', ')
  };

  // Build FAQ schema if post has FAQ content
  const faqBlock = post.content.find(b => b.type === 'faq');
  const faqJsonLd = faqBlock ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqBlock.items.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  } : null;

  const renderContent = (block, idx) => {
    const blockKey = block.type === 'heading' ? `h-${block.text.slice(0, 30)}` : 
                     block.type === 'paragraph' ? `p-${idx}` : `${block.type}-${idx}`;
    switch (block.type) {
      case 'heading':
        if (block.level === 2) {
          return (
            <h2 key={blockKey} id={block.text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+$/, '')} className="font-heading text-2xl sm:text-3xl font-bold text-white mt-12 mb-4 scroll-mt-24">
              {block.text}
            </h2>
          );
        }
        return (
          <h3 key={blockKey} className="font-heading text-xl sm:text-2xl font-semibold text-white mt-8 mb-3">
            {block.text}
          </h3>
        );
      case 'paragraph':
        return (
          <p key={blockKey} className="text-slate-300 leading-[1.8] mb-5 text-base sm:text-[17px]">
            {renderLinkedText(block.text)}
          </p>
        );
      case 'list':
        return (
          <ul key={blockKey} className="space-y-2.5 mb-6 ml-1">
            {block.items.map((item, i) => (
              <li key={`li-${idx}-${i}`} className="flex items-start gap-3 text-slate-300 text-base sm:text-[17px] leading-relaxed">
                <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-orange-400 mt-[10px]" />
                <span>{renderLinkedText(item)}</span>
              </li>
            ))}
          </ul>
        );
      case 'table':
        return (
          <div key={blockKey} className="overflow-x-auto mb-8 mt-6">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  {block.headers.map((h) => (
                    <th key={h} className="text-left px-4 py-3 bg-slate-800/80 text-slate-200 font-semibold border border-slate-700 first:rounded-tl-lg last:rounded-tr-lg">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row, ri) => (
                  <tr key={`row-${idx}-${ri}`}>
                    {row.map((cell, ci) => (
                      <td key={`cell-${idx}-${ri}-${ci}`} className="px-4 py-3 text-slate-300 border border-slate-800 bg-slate-900/40">
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      case 'faq':
        return (
          <div key={blockKey} className="mt-12 mb-8">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-6">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {block.items.map((faq) => (
                <div key={faq.question} className="border border-slate-800 rounded-xl p-5 bg-slate-900/30">
                  <h3 className="font-heading text-lg font-semibold text-white mb-2">{faq.question}</h3>
                  <p className="text-slate-400 leading-relaxed text-[15px]">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] pt-20">
      {/* JSON-LD Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd).replace(/<\/script/gi, '<\\/script') }}
      />
      {faqJsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd).replace(/<\/script/gi, '<\\/script') }}
        />
      )}

      {/* Reading Progress Bar */}
      <div className="fixed top-0 left-0 right-0 z-[60] h-[3px] bg-slate-900">
        <div
          className="h-full bg-gradient-to-r from-blue-500 to-orange-500 transition-all duration-150 ease-out"
          style={{ width: `${progress}%` }}
          data-testid="reading-progress-bar"
        />
      </div>

      {/* Back link */}
      <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 pt-6">
        <Link
          to="/blog"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-300 transition-colors"
          data-testid="blog-back-link"
        >
          <ArrowLeft className="w-4 h-4" />
          All posts
        </Link>
      </div>

      {/* Article Header */}
      <header className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 pt-8 pb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center gap-3 mb-5">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-blue-400 bg-blue-500/10 px-2.5 py-1 rounded-full">
              {post.category}
            </span>
            <span className="flex items-center gap-1 text-xs text-slate-500">
              <Clock className="w-3.5 h-3.5" />
              {post.readTime} min read
            </span>
          </div>

          <h1 className="font-heading text-3xl sm:text-4xl lg:text-[42px] font-bold text-white leading-[1.15] mb-6">
            {post.title}
          </h1>

          <div className="flex items-center gap-4 pb-6 border-b border-slate-800">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-orange-500 flex items-center justify-center text-white text-sm font-bold">
              FK
            </div>
            <div>
              <p className="text-sm font-medium text-slate-200">{post.author}</p>
              <div className="flex items-center gap-3 text-xs text-slate-500">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {formatDate(post.publishDate)}
                </span>
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {post.authorRole}
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </header>

      {/* Hero Image */}
      <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 mb-12">
        <motion.div
          className="rounded-2xl overflow-hidden border border-slate-800"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <img
            src={post.heroImage}
            alt={post.title}
            className="w-full aspect-[2/1] object-cover"
            width={1200}
            height={600}
            loading="eager"
          />
        </motion.div>
      </div>

      {/* Article Body */}
      <article
        ref={articleRef}
        className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 pb-16"
        data-testid="blog-article-content"
      >
        {post.content.map(renderContent)}
      </article>

      {/* Related Links - Internal linking for SEO */}
      <section className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 pb-12">
        <div className="border-t border-slate-800 pt-8">
          <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-4">Related Resources</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <Link to="/use-cases/accounts-payable-automation" className="group flex items-center gap-3 p-4 rounded-xl border border-slate-800 hover:border-slate-700 transition-colors">
              <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                <ArrowRight className="w-4 h-4 text-blue-400" />
              </div>
              <span className="text-sm text-slate-300 group-hover:text-white transition-colors">AP Automation Use Case</span>
            </Link>
            <Link to="/security" className="group flex items-center gap-3 p-4 rounded-xl border border-slate-800 hover:border-slate-700 transition-colors">
              <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                <ArrowRight className="w-4 h-4 text-blue-400" />
              </div>
              <span className="text-sm text-slate-300 group-hover:text-white transition-colors">Security & Compliance</span>
            </Link>
            <Link to="/guides/getting-started" className="group flex items-center gap-3 p-4 rounded-xl border border-slate-800 hover:border-slate-700 transition-colors">
              <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                <ArrowRight className="w-4 h-4 text-blue-400" />
              </div>
              <span className="text-sm text-slate-300 group-hover:text-white transition-colors">Getting Started Guide</span>
            </Link>
            <Link to="/comparisons/plouton-vs-traditional-rpa" className="group flex items-center gap-3 p-4 rounded-xl border border-slate-800 hover:border-slate-700 transition-colors">
              <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                <ArrowRight className="w-4 h-4 text-blue-400" />
              </div>
              <span className="text-sm text-slate-300 group-hover:text-white transition-colors">Plouton vs Traditional RPA</span>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 border-t border-slate-800">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <h3 className="font-heading text-2xl font-bold text-white mb-3">
            Ready to automate your close?
          </h3>
          <p className="text-slate-400 text-sm mb-6 max-w-md mx-auto">
            See how Plouton AI can cut your month-end close time in half. No APIs, no IT projects.
          </p>
          <a href={CAL_LINK} target="_blank" rel="noopener noreferrer">
            <Button
              className="bg-orange-500 text-white hover:bg-orange-600 font-semibold rounded-lg px-8 py-5 text-base"
              data-testid="blog-cta-book-demo"
            >
              Book a Demo
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPostPage;
